import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import TaglineBanner from '@/components/TaglineBanner';
import TrustBadges from '@/components/TrustBadges';
import FadeInUp from '@/components/FadeInUp';
import GoogleRatingBadge from '@/components/GoogleRatingBadge';
import { COURSES, ADDITIONAL_SERVICES, BUSINESS } from '@/lib/site';
import {
  ForkliftIcon, RocketIcon, ReachIcon, BadgeIcon, LicenseIcon, CertificateIcon,
  ArrowRight, ChevronRight, ShieldIcon, StarIcon, HardHatIcon, CheckIcon, PhoneIcon,
} from '@/components/icons';

const ICONS = { forklift: ForkliftIcon, rocket: RocketIcon, reach: ReachIcon, badge: BadgeIcon, license: LicenseIcon, certificate: CertificateIcon };

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden bg-[#060D9C]" data-testid="home-hero">
        <div className="absolute inset-0">
          <motion.img
            src="/generated/hero-warehouse.png"
            alt="Forklift operating in a New Zealand warehouse"
            className="w-full h-full object-cover"
            initial={{ scale: 1.05 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.4, ease: 'easeOut' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#060D9C] via-[#060D9C]/85 to-[#060D9C]/30" />
          <div className="absolute inset-0 bg-noise opacity-30" />
        </div>

        <div className="relative nft-container py-16 lg:py-24 text-white w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="max-w-4xl"
          >
            <div className="label-eyebrow text-[#FC4E2C] mb-4">
              — New Zealand Owned · Est. 1990
            </div>
            <h1 className="font-display font-extrabold uppercase text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95]">
              New Zealand's <br />
              <span className="text-[#FC4E2C]">Most Trusted</span><br />
              Forklift Training
            </h1>
            <p className="mt-6 text-lg lg:text-xl text-white/85 max-w-2xl leading-relaxed">
              Daily public courses in East Tamaki, Auckland. On-site group training nationwide.
              WorkSafeNZ approved. Audited by Competenz. No compromise.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/booking" className="btn-primary-orange" data-testid="home-primary-cta">
                Book a Course <ArrowRight width={18} height={18} />
              </Link>
              <Link to="/courses" className="btn-outline-white" data-testid="home-secondary-cta">
                View All Courses
              </Link>
              <a href={BUSINESS.phoneHref} className="btn-outline-white">
                <PhoneIcon width={16} height={16} /> {BUSINESS.phone}
              </a>
            </div>
            <div className="mt-8 flex items-center gap-3 text-sm text-white/80">
              <GoogleRatingBadge dark={false} />
              <span className="hidden sm:inline opacity-70">— verified by 112 customers</span>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-8 bg-[#F4F5F7]" style={{ clipPath: 'polygon(0 100%, 100% 0, 100% 100%)' }} aria-hidden="true" />
      </section>

      {/* TAGLINE BANNER */}
      <TaglineBanner />

      {/* TRUST BADGES */}
      <section className="nft-container py-12 lg:py-16">
        <FadeInUp>
          <TrustBadges />
        </FadeInUp>
      </section>

      {/* INTRO BLOCK */}
      <section className="nft-container pb-12 lg:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <FadeInUp>
              <div className="label-eyebrow text-[#FC4E2C] mb-3">About NFT</div>
              <h2 className="font-display font-extrabold uppercase text-4xl lg:text-5xl xl:text-6xl leading-[0.95] text-[#060D9C]">
                Dedicated to <span className="text-[#FC4E2C]">providing the best.</span>
              </h2>
              <span className="section-rule mt-5" />
            </FadeInUp>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[#060D9C]/85 leading-relaxed text-base lg:text-lg">
            <FadeInUp delay={0.1}>
              <p>
                Welcome. Our team is committed to providing a friendly, efficient, and the
                best possible service to your team or to you as an individual. NFT is
                <strong> 100% New Zealand owned</strong> and operated.
              </p>
            </FadeInUp>
            <FadeInUp delay={0.15}>
              <p>
                We run <strong>daily public courses</strong> at our East Tamaki training premises in Auckland
                and offer 24/7 on-site service for group bookings, almost anywhere in New Zealand. Flexible
                hours, your equipment, your site.
              </p>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                {['WorkSafeNZ Approved Certificates', 'Photo ID Licence Cards', 'Audited by Competenz', 'On-site or at our facility', 'Renewal & Beginner Streams', 'NZQA Unit Standards Available'].map((b) => (
                  <li key={b} className="flex items-center gap-2 text-sm font-bold uppercase" style={{ fontFamily: 'Barlow Semi Condensed', letterSpacing: '0.05em' }}>
                    <span className="shrink-0 w-6 h-6 rounded-full bg-[#2D8653]/15 text-[#2D8653] flex items-center justify-center">
                      <CheckIcon width={14} height={14} />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="bg-[#060D9C] text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-8 bg-[#F4F5F7]" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 0, 0 100%)' }} aria-hidden="true" />
        <div className="nft-container py-16 lg:py-24">
          <div className="max-w-3xl mb-10">
            <div className="label-eyebrow text-[#FC4E2C] mb-3">Our Services</div>
            <h2 className="font-display font-extrabold uppercase text-4xl lg:text-5xl xl:text-6xl leading-[0.95]">
              Six pillars of <span className="text-[#FC4E2C]">forklift competency.</span>
            </h2>
            <p className="mt-4 text-white/80 text-lg max-w-2xl">
              From beginners taking their first step to operations managers booking renewals —
              we have a stream that fits.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="home-services-grid">
            {COURSES.map((c, i) => {
              const Icon = ICONS[c.icon] || ForkliftIcon;
              return (
                <FadeInUp key={c.title} delay={i * 0.05}>
                  <Link
                    to={`/courses/${c.slug}`}
                    className="group block rounded-2xl bg-white/5 border border-white/10 p-6 hover:bg-white/10 hover:border-[#FC4E2C]/40 transition-colors h-full"
                    data-testid={`home-service-card-${c.slug}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="shrink-0 w-12 h-12 rounded-xl bg-[#FC4E2C]/15 text-[#FC4E2C] flex items-center justify-center group-hover:bg-[#FC4E2C] group-hover:text-[#060D9C] transition-colors">
                        <Icon width={26} height={26} />
                      </div>
                      <div>
                        <h3 className="font-display font-extrabold text-2xl uppercase leading-tight">{c.title}</h3>
                        <div className="label-eyebrow text-white/50 mt-1">{c.duration} · {c.audience}</div>
                      </div>
                    </div>
                    <p className="mt-4 text-white/75 text-sm leading-relaxed">{c.short}</p>
                    <div className="mt-5 inline-flex items-center gap-1 text-[#FC4E2C] text-sm font-bold uppercase" style={{ fontFamily: 'Barlow Semi Condensed', letterSpacing: '0.08em' }}>
                      Learn more <ChevronRight width={14} height={14} />
                    </div>
                  </Link>
                </FadeInUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* ADDITIONAL SERVICES STRIP */}
      <section className="bg-[#FC4E2C] text-white py-3" data-testid="additional-services-strip">
        <div className="nft-container">
          <div className="flex items-center gap-6 overflow-x-auto whitespace-nowrap py-2">
            <span className="label-eyebrow font-extrabold shrink-0">Also offered:</span>
            {ADDITIONAL_SERVICES.map((s, i) => (
              <span key={i} className="label-eyebrow font-bold shrink-0 flex items-center gap-3">
                {s}
                {i < ADDITIONAL_SERVICES.length - 1 && <span className="text-[#060D9C]/40">•</span>}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="nft-container py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-4">
            <div className="label-eyebrow text-[#FC4E2C] mb-3">Trusted by Industry</div>
            <h2 className="font-display font-extrabold uppercase text-4xl lg:text-5xl text-[#060D9C] leading-[0.95]">
              4.9★ Google rating, <span className="text-[#FC4E2C]">112 reviews.</span>
            </h2>
            <p className="mt-4 text-[#060D9C]/75 text-base">
              From sole-trader warehouse owners to logistics teams running multiple sites — the verdict is consistent.
            </p>
            <div className="mt-6">
              <GoogleRatingBadge dark={true} />
            </div>
            <div className="mt-6">
              <a
                href="https://www.google.com/search?q=National+Forklift+Training+Ltd+Auckland"
                target="_blank" rel="noopener noreferrer"
                className="btn-secondary-navy"
                data-testid="see-all-reviews-cta"
              >
                Read all reviews <ArrowRight width={16} height={16} />
              </a>
            </div>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { name: 'Operations Manager', org: 'Auckland Logistics Co.', text: 'NFT have trained our entire warehouse team for over a decade. Friendly, thorough, and absolutely no shortcuts on safety.' },
              { name: 'Owner / Operator', org: 'Family Transport NZ', text: 'Their photo ID licence cards make WorkSafeNZ audits painless. Renewal turnaround is fast and the trainers know their stuff.' },
              { name: 'HR Lead', org: 'East Tamaki Distributor', text: 'Booked a beginner & reach truck combo for new hires. They came back competent and confident. Couldn’t recommend more.' },
            ].map((r, i) => (
              <FadeInUp key={i} delay={i * 0.08}>
                <div className="surface-card p-6 h-full flex flex-col">
                  <div className="flex items-center gap-1 text-[#FC4E2C] mb-3">
                    {[0,1,2,3,4].map(j => <StarIcon key={j} width={16} height={16} />)}
                  </div>
                  <p className="text-[#060D9C]/85 text-sm leading-relaxed flex-1">“{r.text}”</p>
                  <div className="mt-4 pt-4 border-t border-[#060D9C]/10">
                    <div className="font-bold text-sm">{r.name}</div>
                    <div className="label-eyebrow text-[#060D9C]/60">{r.org}</div>
                  </div>
                </div>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="relative bg-[#060D9C] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src="/generated/training-facility.png" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#060D9C] to-[#060D9C]/80" />
        </div>
        <div className="relative nft-container py-16 lg:py-24 text-center">
          <div className="label-eyebrow text-[#FC4E2C] mb-3">Ready to Train?</div>
          <h2 className="font-display font-extrabold uppercase text-4xl lg:text-6xl max-w-3xl mx-auto leading-[0.95]">
            Daily courses run from East Tamaki. <span className="text-[#FC4E2C]">Book yours today.</span>
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/booking" className="btn-primary-orange">
              Book a Course <ArrowRight width={18} height={18} />
            </Link>
            <a href={BUSINESS.phoneHref} className="btn-outline-white">
              <PhoneIcon width={16} height={16} /> {BUSINESS.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
